import { useState, useEffect } from 'react';

type Language = 'es' | 'en';

interface LanguageSelectorProps {
  className?: string;
}

export default function LanguageSelector({ className = '' }: LanguageSelectorProps) {
  const [currentLanguage, setCurrentLanguage] = useState<Language>('es');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') as Language;
    if (savedLanguage && (savedLanguage === 'es' || savedLanguage === 'en')) {
      setCurrentLanguage(savedLanguage);
      document.documentElement.lang = savedLanguage;
    }
  }, []);

  const changeLanguage = (language: Language) => {
    setCurrentLanguage(language);
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
    setIsOpen(false);
    
    // Trigger a custom event to notify components about language change
    window.dispatchEvent(new CustomEvent('languageChange', { detail: language }));
  };

  const languages = [
    { code: 'es' as Language, name: 'Español', flag: '🇪🇸' },
    { code: 'en' as Language, name: 'English', flag: '🇺🇸' }
  ];

  const currentLang = languages.find(lang => lang.code === currentLanguage);

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 bg-gray-900/50 border border-gray-700 hover:border-cyan-500/50 rounded-lg transition-all duration-300 font-mono text-sm text-white hover:bg-gray-800/50 cursor-pointer whitespace-nowrap"
      >
        <span className="text-lg">{currentLang?.flag}</span>
        <span>{currentLang?.name}</span>
        <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}></i>
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-50 min-w-[140px]">
          {languages.map((language) => (
            <button
              key={language.code}
              onClick={() => changeLanguage(language.code)}
              className={`w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-gray-800 transition-colors duration-200 font-mono text-sm cursor-pointer whitespace-nowrap ${
                currentLanguage === language.code 
                  ? 'text-cyan-400 bg-gray-800/50' 
                  : 'text-white'
              } ${language.code === 'es' ? 'rounded-t-lg' : 'rounded-b-lg'}`}
            >
              <span className="text-lg">{language.flag}</span>
              <span>{language.name}</span>
              {currentLanguage === language.code && (
                <i className="ri-check-line text-cyan-400 ml-auto"></i>
              )}
            </button>
          ))}
        </div>
      )}

      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}